import React, { useState, useEffect } from "react";
import "./PlayCockpit.scss";

/**
 * DevourPlay Gaming Cockpit
 * A true gaming interface that feels alive
 */
const PlayCockpit: React.FC = () => {
    const [systemStatus, setSystemStatus] = useState<"initializing" | "online" | "combat-ready">("initializing");
    const [missionData, setMissionData] = useState<any>(null);

    useEffect(() => {
    // Boot sequence animation
        setTimeout(() => setSystemStatus("online"), 1000);
        setTimeout(() => setSystemStatus("combat-ready"), 2000);
    }, []);

    return (
        <div className={`cockpit cockpit--${systemStatus}`}>
            {/* Ambient Background Effects */}
            <div className="cockpit__ambience">
                <div className="cockpit__grid-overlay" />
                <div className="cockpit__scan-line" />
                <div className="cockpit__particles" />
            </div>

            {/* Top HUD Bar */}
            <div className="cockpit__top-hud">
                <div className="hud-element hud-element--left">
                    <div className="pilot-status">
                        <div className="pilot-status__avatar">
                            <img src="/placeholder-avatar.jpg" alt="Pilot" />
                            <div className="pilot-status__rank">LVL 42</div>
                        </div>
                        <div className="pilot-status__stats">
                            <div className="stat stat--xp">
                                <span className="stat__label">COMBAT XP</span>
                                <span className="stat__value">8,896</span>
                            </div>
                            <div className="stat stat--fuel">
                                <span className="stat__label">FUEL</span>
                                <span className="stat__value">$1,058</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="hud-element hud-element--center">
                    <div className="system-status">
                        <div className="system-status__indicator" />
                        <span className="system-status__text">{systemStatus.toUpperCase()}</span>
                    </div>
                </div>

                <div className="hud-element hud-element--right">
                    <div className="squad-status">
                        <div className="squad-status__members">
                            <div className="member member--online" />
                            <div className="member member--online" />
                            <div className="member member--away" />
                            <div className="member member--offline" />
                        </div>
                        <span className="squad-status__count">2/4 ONLINE</span>
                    </div>
                </div>
            </div>

            {/* Main Mission Display */}
            <div className="cockpit__mission-zone">
                <div className="mission-hero">
                    <div className="mission-hero__background">
                        <img src="/mission-bg.jpg" alt="Mission" />
                        <div className="mission-hero__shader" />
                    </div>

                    <div className="mission-hero__content">
                        <div className="mission-hero__header">
                            <span className="mission-type">BRAND MISSION</span>
                            <span className="mission-timer">ENDS IN 23:42:16</span>
                        </div>

                        <h1 className="mission-hero__title">POLARIS-COD CHALLENGE</h1>
                        <p className="mission-hero__description">
                            Complete objectives to earn exclusive rewards and climb the leaderboard
                        </p>

                        <div className="mission-hero__actions">
                            <button className="action-button action-button--primary">
                                <span className="action-button__text">DEPLOY</span>
                                <span className="action-button__hotkey">[SPACE]</span>
                            </button>
                            <button className="action-button action-button--secondary">
                                <span className="action-button__text">INTEL</span>
                                <span className="action-button__hotkey">[I]</span>
                            </button>
                        </div>
                    </div>

                    <div className="mission-hero__urgency">
                        <div className="urgency-indicator urgency-indicator--high">
                            <span className="urgency-indicator__icon">⚠️</span>
                            <span className="urgency-indicator__text">LIMITED TIME</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Command Center */}
            <div className="cockpit__command-center">
                <div className="command-panel command-panel--quests">
                    <h3 className="command-panel__title">ACTIVE OPERATIONS</h3>
                    <div className="quest-list">
                        <div className="quest-item quest-item--active">
                            <div className="quest-item__progress">75%</div>
                            <div className="quest-item__info">
                                <span className="quest-item__name">Daily Combat Training</span>
                                <span className="quest-item__reward">+250 XP</span>
                            </div>
                        </div>
                        <div className="quest-item">
                            <div className="quest-item__progress">30%</div>
                            <div className="quest-item__info">
                                <span className="quest-item__name">Squad Objectives</span>
                                <span className="quest-item__reward">+500 XP</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="command-panel command-panel--radial">
                    <div className="radial-menu">
                        <button className="radial-menu__center">
                            <span className="radial-menu__icon">⚡</span>
                        </button>
                        <div className="radial-menu__items">
                            <button className="radial-item" data-action="loot">
                                <span className="radial-item__icon">💎</span>
                                <span className="radial-item__label">LOOT</span>
                            </button>
                            <button className="radial-item" data-action="shop">
                                <span className="radial-item__icon">🛒</span>
                                <span className="radial-item__label">SHOP</span>
                            </button>
                            <button className="radial-item" data-action="fuel">
                                <span className="radial-item__icon">⚡</span>
                                <span className="radial-item__label">FUEL</span>
                            </button>
                            <button className="radial-item" data-action="squad">
                                <span className="radial-item__icon">👥</span>
                                <span className="radial-item__label">SQUAD</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="command-panel command-panel--activity">
                    <h3 className="command-panel__title">COMBAT LOG</h3>
                    <div className="activity-feed">
                        <div className="activity-entry activity-entry--xp">
                            <span className="activity-entry__time">2m ago</span>
                            <span className="activity-entry__text">Earned 150 XP from Valorant</span>
                        </div>
                        <div className="activity-entry activity-entry--achievement">
                            <span className="activity-entry__time">5m ago</span>
                            <span className="activity-entry__text">🏆 Achievement Unlocked</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Floating Widgets */}
            <div className="cockpit__widgets">
                <div className="widget widget--xp-tracker">
                    <div className="xp-ring">
                        <svg viewBox="0 0 100 100">
                            <circle cx="50" cy="50" r="45" className="xp-ring__bg" />
                            <circle cx="50" cy="50" r="45" className="xp-ring__progress"
                                strokeDasharray="283" strokeDashoffset="70" />
                        </svg>
                        <div className="xp-ring__content">
                            <span className="xp-ring__percent">75%</span>
                            <span className="xp-ring__label">TO NEXT</span>
                        </div>
                    </div>
                </div>

                <div className="widget widget--notifications">
                    <div className="notification notification--new">
                        <span className="notification__badge">NEW</span>
                        <span className="notification__text">Squad invite from DeadEye</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PlayCockpit;